import { LoadingIcon } from "@App/Components/common/Icons";
import { useBrandFilter } from "@App/hooks/brandCombobox/useBrandFilter";
import { type State, actionCreators } from "@App/state";
import type { Brand } from "@App/types";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { bindActionCreators } from "@reduxjs/toolkit";
import type { FunctionComponent } from "react";
import { useDispatch, useSelector } from "react-redux";

type BrandFilterProps = {
  onSubmit?: (brand: Brand | null) => void;
};

export const BrandFilter: FunctionComponent<BrandFilterProps> = ({
  onSubmit,
}: BrandFilterProps) => {
  const organization = useSelector((state: State) => state.orders.organization);
  const brand = useSelector((state: State) => state.filters.brand);
  const dispatch = useDispatch();
  const { changeBrandFilter } = bindActionCreators(actionCreators, dispatch);

  // set brand on change
  const onChange = (e: Brand | null) => {
    changeBrandFilter(e);
    onSubmit?.(e);
  };

  const { popularBrands, regularBrands, loading, setQuery, showDivider } = useBrandFilter({
    organization,
  });

  return (
    <div className="relative">
      <div>
        <div className={"relative md:w-80 max-w-full"}>
          <div className="top-16 mb-10">
            <Combobox
              immediate
              as="div"
              value={brand}
              onChange={(brand) => {
                setQuery("");
                onChange(brand);
              }}
            >
              <div className="relative mt-2">
                <ComboboxInput
                  placeholder={"Marke auswählen"}
                  className="block w-full rounded-md bg-white py-3 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary"
                  onChange={(event) => setQuery(event.target.value)}
                  onBlur={() => setQuery("")}
                  displayValue={(brand: Brand) => brand?.name}
                />
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                  <ChevronDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                </ComboboxButton>

                <ComboboxOptions
                  transition
                  className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline outline-1 outline-black/5 data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                >
                  {loading && (
                    <span className={"flex items-center justify-center py-2 px-3"}>
                      <LoadingIcon className={"size-5 animate-spin"} />
                    </span>
                  )}

                  {popularBrands.map((brand) => (
                    <ComboboxOption
                      key={brand.id}
                      value={brand}
                      className="cursor-default select-none px-3 py-2 text-gray-900 data-[focus]:bg-primary data-[focus]:text-white data-[focus]:outline-none"
                    >
                      <div className="flex items-center">
                        {brand.logo && (
                          <img
                            width={36}
                            height={36}
                            src={`https://werkstatt.repareo.de/storage/${brand.logo}`}
                            alt=""
                            className="size-9 shrink-0 rounded-full bg-white outline outline-1 -outline-offset-1 outline-black/5"
                          />
                        )}
                        <span className="ml-3 block truncate">{brand.name}</span>
                      </div>
                    </ComboboxOption>
                  ))}
                  {showDivider && <div className={"border-t border-gray-200 h-1 mt-1"} />}
                  {regularBrands.map((brand) => (
                    <ComboboxOption
                      key={brand.id}
                      value={brand}
                      className="cursor-default select-none px-3 py-2 text-gray-900 data-[focus]:bg-primary data-[focus]:text-white data-[focus]:outline-none"
                    >
                      <div className="flex items-center">
                        <span className="block truncate">{brand.name}</span>
                      </div>
                    </ComboboxOption>
                  ))}
                </ComboboxOptions>
              </div>
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );
};
